import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { message } from "antd";
import classNames from "classnames";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { MdContentCopy } from "react-icons/md";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { SCREENS } from "utils/constants";
import CanvasIcon from "./CanvasIcon";
import { guestLinkStep } from "./guest-sources";
import { ICON_PATHS } from "./step-look";

const guestLinkOf = (source) =>
  source.guestLink ||
  `${process.env.REACT_APP_BASE_URL}/#${SCREENS.PUBLIC.PROCESSES.INIT.path}/${source.id}`;

const GuestLinkDialog = ({ open, processId, onClose }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const GUEST = t("CANVAS_EDITOR.GUEST_LINK", { returnObjects: true });
  const [pickedId, setPickedId] = useState(null);
  const [confirmedId, setConfirmedId] = useState(null);
  const [createdSource, setCreatedSource] = useState(null);
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const { data, loading, refetch } = useQuery(
    graphql.queries.CANVAS_GUEST_SOURCES,
    {
      variables: {
        where: {
          tracers: { flow: { process: { id: processId } } },
        },
        baseUrl,
      },
      skip: !open || !processId,
      fetchPolicy: "network-only",
    },
  );
  const [generate, { loading: generating }] = useMutation(
    graphql.mutations.GENERATE_PROCESS_GUEST_LINK,
    {
      onCompleted: (result) => {
        const source = get(result, "generateProcessGuestLink");
        if (source) setCreatedSource(source);
        refetch();
      },
    },
  );

  useEffect(() => {
    if (open) return undefined;
    setPickedId(null);
    setConfirmedId(null);
    setCreatedSource(null);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open, onClose]);

  if (!open) return null;

  const listed = get(data, "sources.data") || [];
  const known = createdSource
    ? listed.filter((source) => source.id !== createdSource.id)
    : listed;
  const step = guestLinkStep(
    createdSource ? [...known, createdSource] : known,
    confirmedId,
  );
  const { source } = step;

  const copy = (link) =>
    navigator.clipboard
      .writeText(link)
      .then(() => message.success(GUEST.COPIED))
      .catch(() => message.error(GUEST.COPY_ERROR));

  const generateLink = () =>
    generate({
      variables: {
        where: { id: processId },
        baseUrl,
      },
    });

  const renderLink = (selected) => {
    const link = guestLinkOf(selected);
    return (
      <div className="canvas-guest__link">
        <span className="canvas-guest__name">{selected.name}</span>
        <div className="canvas-guest__row">
          <input
            readOnly
            className="canvas-guest__url"
            value={link}
            aria-label={selected.name}
            onFocus={({ target }) => target.select()}
          />
          <button
            type="button"
            className="canvas-guest__copy"
            aria-label={GUEST.COPY}
            title={GUEST.COPY}
            onClick={() => copy(link)}
          >
            <MdContentCopy size={18} />
          </button>
        </div>
      </div>
    );
  };

  const renderBody = () => {
    if (loading && !createdSource) {
      return <p className="canvas-guest__note">{GUEST.LOADING}</p>;
    }
    if (step.kind === "create") {
      return (
        <div className="canvas-guest__empty">
          <p className="canvas-guest__note">{GUEST.EMPTY}</p>
          <button
            type="button"
            className="canvas-header__button canvas-header__button--primary"
            disabled={generating || !processId}
            onClick={generateLink}
          >
            {generating ? GUEST.GENERATING : GUEST.GENERATE}
          </button>
        </div>
      );
    }
    if (step.kind === "link") return renderLink(source);
    return (
      <div className="canvas-guest__choose">
        <p className="canvas-guest__note">{GUEST.CHOOSE}</p>
        <div className="canvas-guest__choices" role="radiogroup">
          {step.sources.map((item) => (
            <label
              key={item.id}
              className={classNames("canvas-guest__choice", {
                "canvas-guest__choice--selected": pickedId === item.id,
              })}
            >
              <input
                type="radio"
                name="canvas-guest-source"
                value={item.id}
                checked={pickedId === item.id}
                onChange={() => {
                  setPickedId(item.id);
                  setConfirmedId(null);
                }}
              />
              <span className="canvas-guest__choice-name">{item.name}</span>
              {item.platform ? (
                <span className="canvas-guest__platform">{item.platform}</span>
              ) : null}
            </label>
          ))}
        </div>
        <button
          type="button"
          className="canvas-header__button canvas-header__button--primary"
          disabled={!pickedId}
          onClick={() => setConfirmedId(pickedId)}
        >
          {GUEST.GENERATE}
        </button>
        {source ? renderLink(source) : null}
      </div>
    );
  };

  return (
    <>
      <div className="canvas-add__backdrop" onClick={onClose} />
      <div className="canvas-guest" role="dialog" aria-label={GUEST.TITLE}>
        <div className="canvas-add__header">
          <div>
            <h2 className="canvas-add__title">{GUEST.TITLE}</h2>
            <p className="canvas-add__hint">{GUEST.HINT}</p>
          </div>
          <button
            type="button"
            className="canvas-checks__close"
            aria-label={GUEST.CLOSE}
            onClick={onClose}
          >
            <CanvasIcon path={ICON_PATHS.close} strokeWidth={2} />
          </button>
        </div>
        {renderBody()}
      </div>
    </>
  );
};

GuestLinkDialog.propTypes = {
  open: PropTypes.bool,
  processId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

GuestLinkDialog.defaultProps = {
  open: false,
  processId: null,
};

export default GuestLinkDialog;
